import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges
} from '@angular/core'
import { RequestQueryBuilder } from '@nestjsx/crud-request'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Store } from '@ngrx/store'
import moment from 'moment-mini'
import { BehaviorSubject, combineLatest, Observable } from 'rxjs'
import {
  filter,
  first,
  map,
  switchMap,
  take,
  tap,
  withLatestFrom
} from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { bookingSelectors } from 'src/app/features/booking/booking.selectors'
import { CompetitionStandings } from '../../competition'
import { competitionMatchEventSelectors } from '../../competition-match-event/competition-match-event.selectors'
import { competitionMatchActions } from '../../competition-match/competition-match.actions'
import { competitionMatchSelectors } from '../../competition-match/competition-match.selectors'
import { CompetitionPool } from '../../competition-pool/competition-pool'
import { competitionPoolActions } from '../../competition-pool/competition-pool.actions'
import { competitionPoolSelectors } from '../../competition-pool/competition-pool.selectors'
import { competitionSportSelectors } from '../../competition-sport/competition-sport.selectors'
import { competitionSelectors } from '../../competition.selectors'
import { CompetitionService } from '../../competition.service'
import { CompetitionTeam } from '../competition-team'
import { competitionTeamActions } from '../competition-team.actions'
import { competitionTeamSelectors } from '../competition-team.selectors'
import { ActivatedRoute } from '@angular/router'
@UntilDestroy()
@Component({
  selector: 'tables-tab',
  templateUrl: './show.component.html',
  styleUrls: ['./show.component.scss']
})
export class CompetitionTeamTablesTabComponent implements OnInit, OnChanges {
  @Input() competitionId: number
  @Input() public filteredTeams: number[]
  public teamResults$: Observable<any[]>

  public competitionStandings$: BehaviorSubject<CompetitionStandings[]> =
    new BehaviorSubject<CompetitionStandings[]>([])
  public teamId$: Observable<number>
  public team$: Observable<CompetitionTeam>
  public competitionPool$: Observable<CompetitionPool>
  public teamIdsOfTheCompetitionPool: number[] = []
  private isCompetitionPoolFetched: boolean = false

  constructor(
    private store: Store<AppState>,
    public competitionService: CompetitionService,
    private route: ActivatedRoute
  ) {}

  ngOnInit() {
    if (!this.competitionId) {
      this.initForTeam()
    } else {
      if (this.filteredTeams && this.filteredTeams.length) {
        this.teamIdsOfTheCompetitionPool = this.filteredTeams
      }

      this.initForCompetition()
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['filteredTeams'] && !changes['filteredTeams'].firstChange) {
      if (this.filteredTeams && this.filteredTeams.length) {
        this.teamIdsOfTheCompetitionPool = this.filteredTeams
      }
      this.refreshCompetitionStandings()
    }
  }
  private initForCompetition() {
    this.store.dispatch(
      competitionMatchActions.fetchMany({
        params: RequestQueryBuilder.create({
          search: {
            competitionId: this.competitionId
          },
          join: [
            { field: 'booking' },
            { field: 'team1' },
            { field: 'team2' },
            { field: 'competitionMatchEvents' }
          ]
        })
      })
    )

    this.refreshCompetitionStandings()
  }

  private initForTeam() {
    this.teamId$ = this.route.paramMap.pipe(
      map((params) => params.get('teamId')),
      map((id) => Number(id)),
      filter((id): id is number => !isNaN(id))
    )

    this.team$ = this.teamId$.pipe(
      tap((id) => {
        this.store.dispatch(
          competitionTeamActions.fetchOneById({
            id
          })
        )
      }),
      switchMap((teamId) =>
        this.store.select(competitionTeamSelectors.selectById, teamId)
      ),
      tap((team) => {
        if (this.isCompetitionPoolFetched) {
          return
        }
        this.isCompetitionPoolFetched = true
        this.store.dispatch(
          competitionPoolActions.fetchMany({
            params: RequestQueryBuilder.create({
              search: {
                competitionId: team?.competitionId
              },
              join: [{ field: 'competitionTeam' }]
            })
          })
        )
      })
    )

    this.team$.pipe(take(1)).subscribe((team) =>
      this.store.dispatch(
        competitionMatchActions.fetchMany({
          params: RequestQueryBuilder.create({
            search: {
              competitionId: team?.competitionId
            },
            join: [
              { field: 'booking' },
              { field: 'team1' },
              { field: 'team2' },
              { field: 'competitionMatchEvents' }
            ]
          })
        })
      )
    )

    this.competitionPool$ = this.teamId$.pipe(
      switchMap((teamId) =>
        this.store.select(competitionPoolSelectors.selectByFilter, {
          field: 'competitionTeamId',
          value: teamId
        })
      ),
      withLatestFrom(this.team$),
      map(([competitionPools, team]) => {
        return competitionPools.filter(
          (competitionPool) =>
            competitionPool?.competitionId == team.competitionId
        )[0]
      })
    )

    this.competitionPool$
      .pipe(first((competitionPool) => Boolean(competitionPool)))
      .subscribe((competitionPool) => {
        this.store
          .select(competitionPoolSelectors.selectByFilter, {
            field: 'name',
            value: competitionPool?.name
          })
          .pipe(first((competitionPools) => Boolean(competitionPools)))
          .subscribe((competitionPools) => {
            competitionPools.forEach((competitionPool) => {
              this.teamIdsOfTheCompetitionPool.push(
                competitionPool.competitionTeamId
              )
            })
          })
      })

    combineLatest([
      this.store.select(competitionMatchSelectors.selectAll),
      this.store.select(competitionTeamSelectors.selectAll),
      this.store.select(bookingSelectors.selectEntities),
      this.store.select(competitionSelectors.selectEntities),
      this.store.select(competitionSportSelectors.selectEntities),
      this.team$
    ])
      .pipe(
        untilDestroyed(this),
        withLatestFrom(
          this.store.select(competitionMatchEventSelectors.selectAll)
        ),
        map(
          ([
            [
              matches,
              teams,
              bookingEntities,
              competitionEntities,
              competitionSportEntities,
              team
            ],
            competitionMatchEvents
          ]) => {
            const competition = competitionEntities[team?.competitionId]

            const competitionSport =
              competitionSportEntities[competition?.competitionSportId]

            const matchesOfCompetition = matches.filter(
              (match) =>
                moment(bookingEntities[match.bookingId]?.endDate).isBefore(
                  moment()
                ) &&
                (this.teamIdsOfTheCompetitionPool.includes(match.team1Id) ||
                  this.teamIdsOfTheCompetitionPool.includes(match.team2Id))
            )

            const penaltyMatchEvents = competitionMatchEvents.filter(
              (matchEvent) =>
                this.teamIdsOfTheCompetitionPool.includes(
                  matchEvent.competitionTeamId
                ) && matchEvent.eventType.toLowerCase() == 'penalty'
            )

            const teamsOfCompetition = teams?.filter((team) =>
              this.teamIdsOfTheCompetitionPool.includes(team.id)
            )

            const competitionStandings =
              this.competitionService.getTeamStandings(
                teamsOfCompetition,
                matchesOfCompetition,
                competitionSport,
                penaltyMatchEvents
              )
            return competitionStandings
          }
        )
      )
      .subscribe((standings) => {
        this.competitionStandings$.next(standings)
      })
  }
  refreshCompetitionStandings() {
    combineLatest([
      this.store.select(competitionMatchSelectors.selectAll),
      this.store.select(competitionTeamSelectors.selectAll),
      this.store.select(bookingSelectors.selectEntities),
      this.store.select(competitionSelectors.selectEntities),
      this.store.select(competitionSportSelectors.selectEntities),
      this.store.select(competitionMatchEventSelectors.selectAll)
    ])
      .pipe(
        untilDestroyed(this),
        map(
          ([
            matches,
            teams,
            bookingEntities,
            competitionEntities,
            competitionSportEntities,
            competitionMatchEvents
          ]) => {
            const competition = competitionEntities[this.competitionId]

            const competitionSport =
              competitionSportEntities[competition?.competitionSportId]

            let matchesOfCompetition = matches
              .filter((match) => match.competitionId == this.competitionId)
              .filter((match) =>
                moment(bookingEntities[match.bookingId]?.endDate).isBefore(
                  moment()
                )
              )
            if (this.filteredTeams?.length) {
              matchesOfCompetition = matchesOfCompetition.filter(
                (match) =>
                  this.filteredTeams.includes(match.team1Id) ||
                  this.filteredTeams.includes(match.team2Id)
              )
            }

            const competitionMatchIds = matchesOfCompetition.map(
              (match) => match.id
            )

            const penaltyMatchEvents = competitionMatchEvents.filter(
              (matchEvent) =>
                competitionMatchIds.includes(matchEvent.competitionMatchId) &&
                matchEvent.eventType.toLowerCase() == 'penalty'
            )

            const teamsOfCompetition = teams?.filter((team) =>
              matchesOfCompetition.some(
                (match) => match.team1Id == team.id || match.team2Id == team.id
              )
            )

            const competitionStandings =
              this.competitionService.getTeamStandings(
                teamsOfCompetition,
                matchesOfCompetition,
                competitionSport,
                penaltyMatchEvents
              )

            return competitionStandings
          }
        )
      )
      .subscribe((standings) => {
        this.competitionStandings$.next(standings)
      })
  }
}
